import { useMutation, useQuery } from '@apollo/client';
import {
  LoadingPage,
  LoadingPageProps,
  PageLayout,
  PageLayoutProps,
  getCalculateSessionId,
} from '@kizi/comparator-components';
import {
  ComparatorContext,
  healthOrderStatusQueryApollo,
  orderTriplePlayV2,
  useFields,
} from '@kizi/comparator-core';
import React, { useContext, useEffect, useState } from 'react';

type answer = {
  id: string;
  value: string;
};
export interface CheckoutPageProps extends PageLayoutProps {
  loadingPage: LoadingPageProps;
  errorPage: PageLayoutProps;
}

const getValueFromOptions = (field) => {
  const valueId = field.value?.split('field_')[1];
  for (const option of field.options) {
    if (option.value === field.value) {
      return [
        {
          id: valueId,
          value: '1',
        },
      ];
    } else {
      return [
        {
          id: option.value?.split('field_')[1],
          value:
            field.value && !field.value.includes('field_') ? field.value : '1',
        },
      ];
    }
  }
  return [];
};

const getFromValueOrOptions = (field): answer[] => {
  const fieldValues = field.value?.split(',');
  if (fieldValues.length > 1) {
    const answers = [] as answer[];
    for (const value of fieldValues) {
      answers.push({
        id: value?.split('field_')[1],
        value: '1',
      });
    }
    return answers;
  }

  if (field.value?.includes('field_')) {
    return [
      {
        id: field.value?.split('field_')[1],
        value: '1',
      },
    ];
  }

  if ('options' in field && field.options?.length > 0) {
    return getValueFromOptions(field) ?? [];
  }

  const fieldId = field.name?.split('field_')[1];
  return [
    {
      id: fieldId,
      value: field.value,
    },
  ];
};

const isLocalOrTest = () => {
  const location = window.location.hostname;
  const urls = [
    'localhost',
    'test-feature.test-cb.nl',
    'test.test-cb.nl',
    'exampledev.kizi.nl',
  ];
  return urls.includes(location);
};

export const CheckoutPage: React.FC<CheckoutPageProps> = ({
  loadingPage,
  errorPage,
  ...pageLayoutProps
}) => {
  const [
    applyWizardSteps,
    selectedProduct,
    houseNumber,
    houseNumberAddition,
    postcode,
    street,
    city,
  ] = useFields([
    '*.wizardSteps',
    '*.selectedProduct',
    '*.housenumber',
    '*.housenumberaddition',
    '*.postcode',
    '*.street',
    '*.city',
  ]);
  const bodyObject = {
    fields: [
      {
        id: '7',
        value: postcode?.value,
      },
      {
        id: '1',
        value:
          houseNumber?.value +
          (houseNumberAddition?.value ? ' ' + houseNumberAddition?.value : ''),
      },
      {
        id: '9',
        value: street?.value,
      },
      {
        id: '14',
        value: city?.value,
      },
    ] as answer[],
  };
  const [orderId, setOrderId] = useState('');

  if (orderId === 'error') {
    return (
      <PageLayout
        contentStyles={{
          mx: 'auto',
          span: [12, 12, 12, 12],
          width: [null, null, null, 810],
        }}
        {...errorPage}
      />
    );
  }

  const { api } = useContext(ComparatorContext);
  const [postOrder, { loading: createOrderLoading, error: createOrderError }] =
    useMutation(orderTriplePlayV2);

  const {
    loading: statusOrderLoading,
    error: statusOrderError,
    data: statusOrderData,
    stopPolling,
  } = useQuery(healthOrderStatusQueryApollo, {
    variables: { orderId },
    pollInterval: 2000,
    context: {
      headers: {
        'Authorization': 'Bearer ' + api.credentials.token,
      },
    },
    skip: !orderId,
  });

  useEffect(() => {
    const steps = applyWizardSteps?.value ?? ([] as any);
    for (const step of steps) {
      for (const field of step.fields) {
        if (field.name.includes('field_') && field.value) {
          let val = getFromValueOrOptions(field);
          if (val) {
            if (val[0].id === '64') {
              val = [
                {
                  id: '64',
                  value: val[0].value.replace(/\s/g, ''),
                },
              ];
            }
            bodyObject.fields = [...bodyObject.fields, ...val];
          }
        }
      }
    }

    const fields = steps.flatMap((s) => s.fields);
    const userIdField = fields.find((field) => field.name === 'userid');
    const isCbMemberField = fields.find(
      (field) => field.name === 'is_cb_member',
    );
    const membershipCbEmail = fields.find(
      (field) => field.name === 'membership_cb_email',
    );
    const newsletterCb = fields.find((field) => field.name === 'newsletter_cb');

    const product = JSON.parse(selectedProduct?.value as string);
    let postData = {
      variables: {
        orderInput: {
          subscriptionId: parseInt(product.id),
          test: 0,
          email: 1,
          fields: [
            ...bodyObject.fields,
            ...[
              {
                id: 'is_cb_member',
                value: isCbMemberField?.value || 'ja',
              },
              {
                id: 'membership_cb_email',
                value: membershipCbEmail?.value || '',
              },
              {
                id: 'newsletter_cb',
                value: newsletterCb?.value || '0',
              },
            ],
          ],
          comparisonId: product.comparisonId,
          supplierId: product.provider.id,
          userId: userIdField?.value,
          sessionId: getCalculateSessionId(),
        },
      },
    };

    if (isLocalOrTest()) {
      postData.variables.orderInput['ipAddress'] = '1.1.1.1';
      postData.variables.orderInput.test = 1;
    }

    postOrder(postData).then((res) => {
      setOrderId(res.data.addTriplePlayOrder.orderId);
    });
  }, []);

  if (
    createOrderLoading ||
    statusOrderLoading ||
    statusOrderData?.orderStatus?.status === 'QUEUED' ||
    statusOrderData?.orderStatus?.status === 'PROCESSING'
  )
    return <LoadingPage {...loadingPage} />;

  if (
    createOrderError ||
    statusOrderError ||
    statusOrderData?.orderStatus?.status === 'ERROR'
  ) {
    stopPolling();
    return (
      <PageLayout
        contentStyles={{
          mx: 'auto',
          span: [12, 12, 12, 12],
          width: [null, null, null, 810],
        }}
        {...errorPage}
      />
    );
  }

  if (
    statusOrderData?.orderStatus?.status === 'DONE' ||
    statusOrderData?.orderStatus?.status === 'UNDER_REVIEW' ||
    statusOrderData?.orderStatus?.status === 'QUEUED_FOR_SAVING'
  ) {
    localStorage.removeItem(`kizi-comparator-os-triple-play`);
    localStorage.removeItem('apollo-cache-persist');
    localStorage.removeItem('lastProductId');
    stopPolling();
  }

  return (
    <PageLayout
      contentStyles={{
        mx: 'auto',
        span: [12, 12, 12, 12],
        width: [null, null, null, 810],
      }}
      {...pageLayoutProps}
    />
  );
};
